// The practice table's checklist (qa-runs 1163): a panel like any other — on a bar, movable, closable, reopened from
// the top bar's menu — offered only at a practice campaign's table. Its five steps tick themselves from the table's own
// events (`PracticeTracker`, mounted with the table whether the panel is open or not) and can be ticked by hand.
// Reset, after one confirmation, puts the practice table back as it started and unticks them.
import { useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '../Dialogs';
import { useTableState } from '../../table/TableState';
import { resetPractice, type TableResult } from '../../table';
import { PRACTICE_STEPS, onTicks, tick, ticked, untickAll, type PracticeStep } from '../../practice';

export const PRACTICE_RESET_ASK = 'Put the practice table back as it started?';
const btn = 'rounded bg-stone-800 px-2 py-1 text-xs text-stone-200 hover:bg-stone-700 disabled:opacity-50';

function useTicks(campaignId: string): PracticeStep[] {
  const [, bump] = useState(0);
  useEffect(() => onTicks(() => bump((n) => n + 1)), []);
  return ticked(campaignId);
}

/**
 * The steps' ticks from what reaches this browser: a token's place changed, the roll log grew (not a page of history
 * loaded), the turn order started, the turn passed while it ran, the revealed squares changed. A rewind's jump is
 * none of them. Renders nothing.
 */
/** Whose turn it is, in either shape of the turn order (the DM's index, a player's id). */
const turnOf = (e: object): unknown => ('activeIndex' in e ? (e as { activeIndex: number }).activeIndex : (e as { active?: unknown }).active);

export function PracticeTracker({ campaignId }: { campaignId: string }) {
  const { store, log, logLoads } = useTableState();
  const prev = useRef<{ state: typeof store.state; rewinds: number; logLength: number; logLoads: number } | null>(null);
  useEffect(() => {
    const was = prev.current;
    const now = { state: store.state, rewinds: store.rewinds ?? 0, logLength: log.length, logLoads };
    prev.current = now;
    if (!was || was.rewinds !== now.rewinds) return;
    const a = was.state;
    const b = now.state;
    if (Object.values(b.objects).some((o) => { const before = a.objects[o.id]; return before && (before.x !== o.x || before.y !== o.y); })) tick(campaignId, 'move');
    if (was.logLoads === now.logLoads && now.logLength > was.logLength) tick(campaignId, 'roll');
    const ea = a.encounter;
    const eb = b.encounter;
    if (eb?.running && !ea?.running) tick(campaignId, 'fight');
    if (eb?.running && ea?.running && (turnOf(eb) !== turnOf(ea) || eb.round !== ea.round)) tick(campaignId, 'turn');
    const fa = (a as { fog?: { revealed?: unknown; rev?: number } }).fog;
    const fb = (b as { fog?: { revealed?: unknown; rev?: number } }).fog;
    if (fb && fa && fb.rev !== fa.rev && JSON.stringify(fb.revealed ?? []).length > JSON.stringify(fa.revealed ?? []).length) tick(campaignId, 'fog');
  }, [store, log, logLoads, campaignId]);
  return null;
}

export function ChecklistPanel({ campaignId, onResult }: { campaignId: string; onResult?: (r: TableResult) => void }) {
  const done = useTicks(campaignId);
  const { role } = useTableState();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState('');
  const all = PRACTICE_STEPS.every((s) => done.includes(s.id));

  const reset = async () => {
    setAsking(false);
    setBusy(true);
    setSaid('');
    // The scene is laid out again while the reset runs: those changes are not the DM's steps.
    untickAll(campaignId, 60_000);
    const r = await resetPractice(campaignId);
    untickAll(campaignId, 2_000);
    setBusy(false);
    if (r.kind === 'ok') { setSaid('The practice table is back as it started.'); onResult?.(r); } else setSaid(r.kind === 'refused' && r.reason ? r.reason : 'The practice table could not be reset just now. Try again in a moment.');
  };

  return (
    <div className="space-y-3 p-2 text-sm text-stone-200" data-testid="practice-checklist">
      <p className="text-xs text-stone-400">The basics, in about five minutes. Each step ticks itself off when you do it.</p>
      <ol className="space-y-2">
        {PRACTICE_STEPS.map((s, k) => {
          const on = done.includes(s.id);
          return (
            <li key={s.id} className="flex items-start gap-2" data-testid={`practice-step-${s.id}`} data-done={on ? 'yes' : 'no'}>
              <input id={`practice-${s.id}`} type="checkbox" className="mt-1" checked={on} onChange={(e) => tick(campaignId, s.id, e.target.checked)} />
              <label htmlFor={`practice-${s.id}`} className="flex-1">
                <span className={on ? 'line-through text-stone-400' : ''}>{k + 1}. {s.label}</span>
                {!on && <span className="block text-xs text-stone-500">{s.hint}</span>}
              </label>
            </li>
          );
        })}
      </ol>
      {all && <p className="text-xs text-emerald-300" role="status">All five done. The table is yours to play with: anything you try here, Reset puts back.</p>}
      {role === 'dm' && (
        <div className="space-y-1 border-t border-stone-800 pt-2">
          <button type="button" className={btn} disabled={busy} onClick={() => setAsking(true)} data-testid="practice-reset">{busy ? 'Resetting…' : 'Reset'}</button>
          <p className="text-xs text-stone-500">Puts the scene, tokens, fog, sample sheets and this list back. Your uploaded pictures and invited friends stay.</p>
        </div>
      )}
      {said && <p className="text-xs text-amber-300" role="status">{said}</p>}
      {asking && (
        <ConfirmDialog title="Reset the practice table" body={PRACTICE_RESET_ASK} confirmLabel="Reset" destructive
          onConfirm={() => { void reset(); }} onClose={() => setAsking(false)} />
      )}
    </div>
  );
}
